import api, { iotAPI } from '@/api'

const state = {
  reports: {},
  metrics: [],
  pagination: null,
  selectedMetric: null,
  metricData: null,
  loading: false,
  loadingMetricData: false,
  error: null
}

const mutations = {
  SET_REPORT(state, { type, data }) {
    state.reports = { ...state.reports, [type]: data }
  },
  SET_METRICS(state, { metrics, pagination }) {
    state.metrics = metrics
    state.pagination = pagination
  },
  SET_SELECTED_METRIC(state, metric) {
    state.selectedMetric = metric
  },
  SET_METRIC_DATA(state, data) {
    state.metricData = data
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_LOADING_METRIC_DATA(state, loading) {
    state.loadingMetricData = loading
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  CLEAR_ERROR(state) {
    state.error = null
  }
}

const actions = {
  async generateTemperatureReport({ commit }, params = {}) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await api.get('/reports/temperature', { params })
      commit('SET_REPORT', { type: 'temperature', data: response.data })
      
      return response.data
    } catch (error) {
      commit('SET_ERROR', 'Error al generar reporte de temperatura')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async generateHumidityReport({ commit }, params = {}) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await api.get('/reports/humidity', { params })
      commit('SET_REPORT', { type: 'humidity', data: response.data })
      
      return response.data
    } catch (error) {
      commit('SET_ERROR', 'Error al generar reporte de humedad')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async generateAlertsReport({ commit }, params = {}) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await api.get('/reports/alerts', { params })
      commit('SET_REPORT', { type: 'alerts', data: response.data })
      
      return response.data
    } catch (error) {
      commit('SET_ERROR', 'Error al generar reporte de alertas')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async generateDeviceReport({ commit }, deviceId) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await api.get(`/reports/devices/${deviceId}`)
      commit('SET_REPORT', { type: `device_${deviceId}`, data: response.data })
      
      return response.data
    } catch (error) {
      commit('SET_ERROR', 'Error al generar reporte del dispositivo')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async exportReport({ commit }, { type, params = {} }) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      const response = await api.get(`/reports/export/${type}`, { 
        params, 
        responseType: 'blob' 
      })
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `report_${type}_${new Date().toISOString().split('T')[0]}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      
      return { success: true }
    } catch (error) {
      commit('SET_ERROR', 'Error al exportar reporte')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async getMetrics({ commit }, filters = {}) {
    try {
      commit('SET_LOADING', true)
      commit('CLEAR_ERROR')
      
      // Searching metrics with filters - handled silently
      const response = await iotAPI.getMetrics(filters)
      // API response received - handled silently
      
      const metrics = response.data.data.metrics || []
      const pagination = response.data.data.pagination || null
      // Metrics found - handled silently
      // Pagination data - handled silently
      
      commit('SET_METRICS', { metrics, pagination })
      
      return response.data
    } catch (error) {
      // Error getting metrics - handled silently
      commit('SET_ERROR', 'Error al obtener métricas')
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async getMetricData({ commit }, { metricId, params = {} }) {
    try {
      commit('SET_LOADING_METRIC_DATA', true)
      commit('CLEAR_ERROR')
      
      // Starting complete historical data load - handled silently
      const startTime = Date.now()
      
      const response = await iotAPI.getMetricData(metricId, params)
      
      const endTime = Date.now()
      const duration = ((endTime - startTime) / 1000).toFixed(2)
      // Data loaded successfully - handled silently
      
      commit('SET_METRIC_DATA', response.data.data)
      
      return response.data
    } catch (error) {
      // Error loading data - handled silently
      if (error.code === 'ECONNABORTED') {
        commit('SET_ERROR', 'La consulta tardó demasiado tiempo. Intenta con un rango de fechas más pequeño.')
      } else {
        commit('SET_ERROR', 'Error al obtener datos de la métrica')
      }
      throw error
    } finally {
      commit('SET_LOADING_METRIC_DATA', false)
    }
  },



  async searchDevices({ commit }, searchTerm) {
    try {
      if (!searchTerm || searchTerm.length < 3) {
        return { data: { data: [] } }
      }
      
      const response = await iotAPI.searchDevices(searchTerm)
      return response.data
    } catch (error) {
      // Error searching devices - handled silently
      return { data: { data: [] } }
    }
  },

  selectMetric({ commit }, metric) {
    commit('SET_SELECTED_METRIC', metric)
  },

  clearMetricData({ commit }) {
    commit('SET_METRIC_DATA', null)
    commit('SET_SELECTED_METRIC', null)
  },

  async initialize({ dispatch }) {
    try {
      // Initialize with default reports
      const defaultParams = {
        startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        endDate: new Date().toISOString()
      }
      
      await Promise.all([
        dispatch('generateTemperatureReport', defaultParams),
        dispatch('generateHumidityReport', defaultParams),
        dispatch('generateAlertsReport', defaultParams)
      ])
    } catch (error) {
      // Error initializing reports module - handled silently
    }
  }
}

const getters = {
  reports: state => state.reports,
  metrics: state => state.metrics,
  pagination: state => state.pagination,
  selectedMetric: state => state.selectedMetric,
  metricData: state => state.metricData,
  loading: state => state.loading,
  loadingMetricData: state => state.loadingMetricData,
  error: state => state.error,
  
  getReport: state => type => {
    return state.reports[type] || null
  },
  
  getTemperatureReport: state => {
    return state.reports.temperature || null
  },
  
  getHumidityReport: state => {
    return state.reports.humidity || null
  },
  
  getAlertsReport: state => {
    return state.reports.alerts || null
  },
  
  getDeviceReport: state => deviceId => {
    return state.reports[`device_${deviceId}`] || null
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
} 


// WEBPACK FOOTER //
// ./src/store/modules/reports.js